import { Cross2Icon, PlusIcon } from "@radix-ui/react-icons";
import {
  Badge,
  Box,
  Flex,
  Heading,
  IconButton,
  RadioCards,
  Text,
} from "@radix-ui/themes";
import { twMerge } from "tailwind-merge";

import PolygonCrop from "./polygon-crop";

type Projection = {
  image: {
    compressed: string;
    original: string;
    width: number;
    height: number;
  } | null;
  cameraId: string;
  cameraSkuSection: any | undefined;
};

type ProjectionsProps = {
  projections: Projection[];
  allowDelete?: boolean;
  value: string;
  getIsSelected: (projection: Projection) => boolean;
  getIsMain?: (projection: Projection) => boolean;
  onProjectionClick?: (projection: Projection) => void;
  onDeleteProjection?: (projection: Projection) => void;
};

export function Projections({
  value,
  projections,
  getIsSelected,
  getIsMain,
  onProjectionClick,
  allowDelete,
  onDeleteProjection,
}: ProjectionsProps) {
  return (
    <Box>
      <Flex>
        <Heading size="1">Projections</Heading>
      </Flex>
      <RadioCards.Root
        value={value}
        onValueChange={(newValue) => {
          const projection = projections.find(
            ({ cameraId }) => cameraId === newValue
          );

          if (projection) onProjectionClick?.(projection);
        }}
        columns={{ initial: "2" }}
        gap="1"
      >
        {projections?.map((projection) => {
          const { cameraId, cameraSkuSection, image } = projection;
          const isSelected = getIsSelected(projection);

          return (
            <div key={cameraId} className="relative">
              <RadioCards.Item
                disabled={getIsMain?.(projection)}
                value={cameraId}
                className={twMerge(
                  "basis-1/2 block m-0 p-0 text-left w-full h-full",
                  isSelected ? "" : ""
                )}
              >
                {getIsMain?.(projection) && (
                  <Badge
                    size="1"
                    className="absolute bottom-1 right-1 text-[10px] p-0 px-1"
                    variant="solid"
                    color="green"
                  >
                    Main
                  </Badge>
                )}
                <div className="aspect-square p-1">
                  {!image ? (
                    <div className="w-full h-full bg-gray-200 flex justify-center items-center rounded-md">
                      <Text size="1" color="gray">
                        Camera image doesn't exist
                      </Text>
                    </div>
                  ) : cameraSkuSection ? (
                    <PolygonCrop
                      imageUrl={image.original}
                      polygonPoints={cameraSkuSection.polygon_points}
                      originalWidth={image.width}
                      originalHeight={image.height}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex justify-center items-center rounded-md">
                      <PlusIcon />
                    </div>
                  )}
                </div>

                <Flex px="1" pb="1" gap="1">
                  <Text size="1" as="span" color="gray">
                    Camera:
                  </Text>
                  <Text size="1" as="span" color="gray" highContrast>
                    {cameraId}
                  </Text>
                </Flex>
              </RadioCards.Item>
              {cameraSkuSection && !getIsMain?.(projection) && allowDelete && (
                <IconButton
                  size="1"
                  className="absolute bottom-1.5 right-2 z-[99999]"
                  variant="ghost"
                  color="gray"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteProjection?.(projection);
                  }}
                >
                  <Cross2Icon />
                </IconButton>
              )}
            </div>
          );
        })}
      </RadioCards.Root>
    </Box>
  );
}
